import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { Loader2, Lock, CheckCircle, Eye, EyeOff, ArrowLeft, AlertCircle } from 'lucide-react';

/**
 * Email-based password reset — user arrives here from the reset link
 * emailed to them (e.g. /reset-password?token=abc123).
 */
export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError('Missing or invalid reset link.');
  }, [token]);

  const handleReset = async () => {
    if (!token) return;
    if (!newPassword || newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (!/[A-Z]/.test(newPassword)) { setError('Password must contain at least 1 uppercase letter'); return; }
    if (!/[0-9]/.test(newPassword)) { setError('Password must contain at least 1 number'); return; }

    setLoading(true); setError('');
    try {
      await api.post('/auth/forgot-password/reset-via-email', { reset_token: token, new_password: newPassword });
      setDone(true);
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 flex items-center justify-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <div className="w-full max-w-md px-6 py-10">
        <Link to="/login" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6" data-testid="back-to-login">
          <ArrowLeft size={16} /> Back to Login
        </Link>

        {done ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center" data-testid="email-reset-success">
            <CheckCircle size={40} className="text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Password Reset Successful</h2>
            <p className="text-sm text-slate-600 mb-6">Your password has been updated. You can now log in.</p>
            <button onClick={() => navigate('/login')} className="w-full py-3 rounded-full bg-sky-600 text-white font-semibold text-sm hover:bg-sky-700" data-testid="go-to-login">
              Go to Login
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 space-y-6">
            <div>
              <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Set New Password</h2>
              <p className="text-sm text-slate-500 mt-1">Create a strong new password for your DocSite account.</p>
            </div>

            {!token ? (
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 flex items-start gap-2" data-testid="invalid-link">
                <AlertCircle size={14} className="text-rose-500 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-rose-700">
                  <p className="font-medium">This link is missing the reset token.</p>
                  <p className="mt-1">Please request a new password reset from the <Link to="/forgot-password" className="underline">forgot password page</Link>.</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">New Password</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input type={showNewPassword ? 'text' : 'password'}
                      className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                      value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 8 chars, 1 uppercase, 1 number" data-testid="new-password-input" />
                    <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" aria-label="Toggle password visibility">
                      {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">Confirm Password</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input type={showConfirmPassword ? 'text' : 'password'}
                      className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                      value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter password" data-testid="confirm-password-input" />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" aria-label="Toggle password visibility">
                      {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <button onClick={handleReset} disabled={loading || !token}
                  className="w-full py-3 rounded-full bg-sky-600 text-white font-semibold text-sm hover:bg-sky-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="set-password-btn">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
                  {loading ? 'Resetting...' : 'Set New Password'}
                </button>
              </div>
            )}

            {error && (
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 flex items-start gap-2" data-testid="reset-error">
                <AlertCircle size={14} className="text-rose-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-rose-700">{error}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
